import { UserRole } from '@cc/schema';
import { applyLocale, useRefetchOnLocaleChange } from './providers/localeSwitch';
import { ErrorComponent, useNotificationProvider } from '@refinedev/antd';
import { Authenticated, Refine, type DataProvider } from '@refinedev/core';
import dataProvider from '@refinedev/graphql';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { useMachine } from '@xstate/react';
import { App as AntdApp, ConfigProvider } from 'antd';
import enUS from 'antd/lib/locale/en_US';
import ruRU from 'antd/lib/locale/ru_RU';
import {
  ApartmentOutlined,
  AppstoreOutlined,
  BookOutlined,
  CalendarOutlined,
  DashboardOutlined,
  MailOutlined,
  SettingOutlined,
  TeamOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import routerProvider, {
  NavigateToResource,
  UnsavedChangesNotifier,
} from '@refinedev/react-router-v6';
import { throttle } from 'lodash';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from 'react-router-dom';
import {
  useClientSessionTimerState,
  type ClientSessionTimerApi,
} from './hooks/useClientSessionTimer';
import { CaseloadOverviewPage } from './pages/caseload/CaseloadOverviewPage';
import { AllRecordsPage } from './pages/caseload/AllRecordsPage';
import { CaseloadTabs } from './pages/caseload/CaseloadTabs';
import { LegacyToolListRedirect } from './pages/caseload/LegacyToolListRedirect';
import { MyToolsPage } from './pages/settings/MyToolsPage';
import {
  firebaseAuth,
  httpClient,
  updateHeaderToken,
} from './providers/authProvider';
import { withNormalizedErrors } from './providers/normalizeDataError';
import {
  createTherapistActivityMachine,
  TherapistActivityService,
} from './machines/therapistActivityMachine';
import { AbcModelCreate } from './pages/abcModels';
import {
  BehaviorExperimentCreate,
  BehaviorExperimentView,
} from './pages/behaviorExperiments';
import { ClientList, UnassignedClientList } from './pages/clients';
import { ClientShow } from './pages/clients/show';
import { CreateConceptualization } from './pages/conceptualization/create';
import { DownwardArrowCreate } from './pages/downwardArrow';
import { ExposureCreate } from './pages/exposure';
import { EditExposureSituation } from './pages/exposure/edit';
import { InitialSetup } from './pages/initialSetup';
import { InviteList } from './pages/invites';
import { CreateInvite } from './pages/invites/create';
import { InviteJoin } from './pages/invites/InviteJoin';
import { LoginPage } from './pages/login';
import { MessageList } from './pages/messages';
import { BackofficeLayout } from './pages/backoffice/BackofficeLayout';
import { BackofficeMembers } from './pages/backoffice/members';
import { BackofficeErase } from './pages/backoffice/erasure/erase';
import { BackofficeErasureLog } from './pages/backoffice/erasure/log';
import { PasswordResetPage } from './pages/passwordReset';
import { CreateCopingCard } from './pages/portalCards/create';
import { PortalSubscriptionList } from './pages/portalSubscription';
import { CreateThought } from './pages/portalThoughts/create';
import { PracticeEdit } from './pages/practice/edit';
import { PracticeHomeworkBoardPage } from './pages/homework/PracticeHomeworkBoardPage';
import { WorksheetLibraryPage } from './pages/worksheets/WorksheetLibraryPage';
import { WorksheetBuilderPage } from './pages/worksheets/WorksheetBuilderPage';
import { WorksheetOverTimePage } from './pages/worksheets/WorksheetOverTimePage';
import { WorksheetResponsePage } from './pages/worksheets/WorksheetResponsePage';
import { WorksheetPrintView } from './pages/worksheets/WorksheetPrintView';
import { PathwayLibraryPage } from './pages/pathways/PathwayLibraryPage';
import { PathwayBuilderPage } from './pages/pathways/PathwayBuilderPage';
import { PathwayOutcomesPage } from './pages/pathways/PathwayOutcomesPage';
import { PathwayProgressPage } from './pages/pathways/PathwayProgressPage';
import {
  BackofficeWorksheetPublish,
  BackofficeWorksheets,
} from './pages/backoffice/worksheets';
import { BackofficeDiaryCardTemplates } from './pages/backoffice/diaryCardTemplates';
import { BackofficeInstruments } from './pages/backoffice/instruments'; // E35
// E41
import { BackofficeActivityLibrary } from './pages/backoffice/activityLibrary';
// E36
import { BackofficeValuesDecks } from './pages/backoffice/valuesDecks';
import { BackofficeCourses } from './pages/backoffice/courses';
import { BackofficeCourseReview } from './pages/backoffice/courseReview';
import {
  BackofficePracticeCapabilities,
  BackofficePractices,
} from './pages/backoffice/practices';
import { ProfileEdit } from './pages/profile/edit';
import { SessionsList } from './pages/sessions';
import { SessionsCreate } from './pages/sessions/create';
import { SignupPage } from './pages/signup';
import { SupportingCyclesCreate } from './pages/supportingCycles';
import { UserList } from './pages/users';
import { VerifyEmail } from './pages/verifyEmail';
import { WorryTreeCreate } from './pages/worryTree';
import { CcThemedLayoutV2 } from './components/layout';
import { PortalUpdateBanner } from './components/PortalUpdateBanner';
import { sereneShellTheme } from './styles/serene-shell.theme';
import '@refinedev/antd/dist/reset.css';
import './styles/override.css';

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [new BrowserTracing()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
  });
}

// F-177: every rejection leaves the provider as a readable `HttpError` (providers/normalizeDataError.ts)
export const gqlDataProvider = withNormalizedErrors(
  dataProvider(httpClient) as Required<DataProvider>,
);

// Note: for some strange reason the context needs to be setup in App.tsx
// Doing it in a separate file and importing it does not work, breaks refine
export const TherapistActivityReactContext =
  createContext<TherapistActivityService>(null!);

/**
 * E57 · the ONE client-session timer (Q-e57g1-2 A) — the client page's Start Client Session and the header bar's share
 * it, pinned to the client it was started for (`hooks/useClientSessionTimer.ts`). Defined here beside the activity
 * context for the same reason (see the note above).
 */
export const ClientSessionTimerContext = createContext<ClientSessionTimerApi>(
  null!,
);

export const TherapistActivityProvider = (props: any) => {
  const therapistActivityMachine = useMemo(
    () =>
      createTherapistActivityMachine({
        gqlDataProvider,
      }),
    [],
  );

  const [
    therapistActivityState,
    therapistActivitySend,
    therapistActivityService,
  ] = useMachine(therapistActivityMachine);

  const sessionTimer = useClientSessionTimerState(gqlDataProvider);

  return (
    <TherapistActivityReactContext.Provider value={therapistActivityService}>
      <ClientSessionTimerContext.Provider value={sessionTimer}>
        {props.children}
      </ClientSessionTimerContext.Provider>
    </TherapistActivityReactContext.Provider>
  );
};

const LocaleRefetch = ({ language }: { language: string | undefined }) => {
  useRefetchOnLocaleChange(language);
  return null;
};

function App() {
  const { t, i18n } = useTranslation();

  const [ready, setReady] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setReady(true);
    }, 500);
  }, []);

  const i18nProvider = {
    translate: (key: string, params: object) => t(key, params),
    changeLocale: (lang: string) => applyLocale(lang, i18n, httpClient),
    getLocale: () => i18n.languages[0],
  };

  useEffect(() => {
    httpClient.setHeader('Accept-Language', i18n.languages[0]);
  }, [i18n.languages[0]]);

  // Refreshing Firebase auth token
  useEffect(() => {
    const interval = setInterval(() => {
      updateHeaderToken();
    }, 1000 * 60 * 10);
    return () => clearInterval(interval);
  }, []);

  // Note: it reduces multiple simultaneous calls to getIdentity
  const throttledGetIdentity = throttle(async () => {
    const identity = await firebaseAuth?.getIdentity?.();
    return identity;
  }, 500);

  useEffect(() => {
    setTimeout(() => setReady(true), 500);
  }, []);

  return ready ? (
    <BrowserRouter>
      <TherapistActivityProvider>
        <ConfigProvider
          locale={i18n.languages[0]?.startsWith('ru') ? ruRU : enUS}
          theme={sereneShellTheme}
        >
          <AntdApp>
            {/**
             * E30 · Portal Build Freshness. It renders nothing — it watches `/version.json` for the
             * whole tab and opens the antd notification when the served build id differs from the
             * one baked into this bundle. Here rather than inside `Refine` so the watch is not tied
             * to a route, and inside `AntdApp` because that is what `notification` comes from.
             */}
            <PortalUpdateBanner />
            <Refine
              accessControlProvider={{
                can: async ({ resource, action, params, ...rest }) => {
                  const identity = await throttledGetIdentity();

                  const notAuthorizedForUser = [
                    'practice',
                    'practiceusers',
                    'practiceinfo',
                    'practiceInfo',
                    'portalSubscriptions',
                    // E57 · PortalMenu — the practice pool and the subscription are an administrator's entries (the API
                    // refuses anyone else); the sider asks with the LOWER-CASED resource name
                    'unassignedclients',
                    'portalsubscriptions',
                  ];

                  if (
                    notAuthorizedForUser.includes(resource || '') &&
                    // @ts-ignore
                    identity?.db?.role !== UserRole.THERAPIST_ADMIN
                  ) {
                    return {
                      can: false,
                      reason: t('notifications.unauthorized'),
                    };
                  }

                  // const inReview = ['behaviorExperiments'];
                  // const allowedUsers = ['users/45', 'users/233438', 'users/234162'];

                  // if (
                  //   inReview.includes(resource) &&
                  //   !allowedUsers.includes(identity?.db?._id)
                  // ) {
                  //   return {
                  //     can: false,
                  //     reason: t('notifications.unauthorized'),
                  //   };
                  // }

                  return {
                    can: true,
                    reason: '',
                  };
                },
              }}
              notificationProvider={useNotificationProvider}
              routerProvider={routerProvider}
              dataProvider={gqlDataProvider}
              authProvider={firebaseAuth}
              resources={[
                /**
                 * E57 · THE GLOBAL MENU (founder Q-pia-7 A as answered 2026-10-17, Q-e57g1-7 A): daily clinical work
                 * on top — Clients · Caseload · Sessions · Messages · Library (Worksheets, Pathways) — then Practice
                 * (administrator) and Settings (My profile, My tools, Subscription for an administrator, Invites) at the
                 * bottom. Unassigned clients stays its own entry for an administrator (2026-09-14). The old Tools group
                 * is gone from the menu: its ten lists are Caseload › All records, and their routes stay registered
                 * (hidden) so the create pages, CreateButton and the old addresses keep resolving (Q-pia-3 A). Today and
                 * Calendar join later with scheduling (Q-pia-8 A). No badge or count on any entry (Q-pia-4 A).
                 */
                {
                  name: 'clients',
                  list: '/clients',
                  show: '/clients/show/:id',
                  meta: {
                    icon: <TeamOutlined />,
                  },
                },
                /**
                 * E07 (M7) — the PRACTICE POOL: clients released by a departing member, waiting for a new practitioner.
                 * Its OWN sidebar entry, deliberately (founder, 2026-09-14: "default should be the regular client list,
                 * and unassigned — a separate menu item"); E57: an administrator's entry.
                 */
                {
                  name: 'unassignedClients',
                  list: '/clients/unassigned',
                  meta: {
                    icon: <UserSwitchOutlined />,
                    label: t('portalClients.unassigned.title'),
                  },
                },
                // E57 · Caseload — Overview · Homework (the E34 board, at its /homework address) · All records
                {
                  name: 'caseload',
                  list: '/caseload',
                  meta: {
                    icon: <DashboardOutlined />,
                    label: t('portalMenu.caseload'),
                  },
                },
                {
                  name: 'caseloadRecords',
                  list: '/caseload/records',
                  meta: { hide: true, label: t('allRecords.title') },
                },
                /**
                 * E34 · the Homework board — Caseload's Homework tab now (E57), same address, unchanged. Hidden from the
                 * menu: Caseload is the entry.
                 */
                {
                  name: 'homework',
                  list: '/homework',
                  meta: {
                    hide: true,
                    label: t('homework.board.menu'),
                  },
                },
                {
                  name: 'sessions',
                  create: '/sessions/create',
                  list: '/sessions',
                  meta: {
                    label: t('pages.sessions.title'),
                    icon: <CalendarOutlined />,
                  },
                },
                {
                  name: 'messages',
                  list: 'messages',
                  meta: {
                    label: t('messages.messages'),
                    icon: <MailOutlined />,
                  },
                },
                // E57 · Library — the practice's worksheets and pathways (E34 blocks 2a / 3a)
                {
                  name: 'library',
                  meta: {
                    label: t('portalMenu.library'),
                    icon: <BookOutlined />,
                  },
                },
                {
                  parentName: 'library',
                  name: 'worksheets',
                  list: '/worksheets',
                  meta: {
                    label: t('worksheets.library.menu'),
                  },
                },
                {
                  parentName: 'library',
                  name: 'pathways',
                  list: '/pathways',
                  meta: {
                    label: t('pathways.library.menu'),
                  },
                },
                // the ten tool resources stay registered for their create pages and CreateButton — hidden from the menu
                {
                  name: 'conceptualizations',
                  create: '/tools/conceptualizations/create',
                  list: '/tools/conceptualizations',
                  meta: {
                    hide: true,
                    label: t('pages.conceptualization.conceptualizations'),
                  },
                },
                {
                  name: 'portalCards',
                  list: '/tools/portalCards',
                  create: '/tools/portalCards/create',
                  meta: { hide: true, label: t('pages.copingCards.title') },
                },
                {
                  name: 'abcModels',
                  create: '/tools/abcModels/create',
                  list: '/tools/abcModels',
                  meta: { hide: true, label: t('shared.abcModels.title') },
                },
                {
                  name: 'portalThoughts',
                  list: '/tools/portalThoughts',
                  create: '/tools/portalThoughts/create',
                  meta: { hide: true, label: t('pages.thoughtList.title') },
                },
                {
                  name: 'worryTrees',
                  create: '/tools/worryTrees/create',
                  list: '/tools/worryTrees',
                  meta: { hide: true, label: t('shared.worryTree.worryTrees') },
                },
                {
                  name: 'downwardArrows',
                  create: '/tools/downwardArrows/create',
                  list: '/tools/downwardArrows',
                  meta: {
                    hide: true,
                    label: t('shared.downwardArrows.downwardArrows'),
                  },
                },
                {
                  name: 'behaviorExperiments',
                  create: '/tools/behaviorExperiments/create',
                  list: '/tools/behaviorExperiments',
                  // E02: the experiment view (design + observations + completion) for log-owned rows
                  show: '/tools/behaviorExperiments/show',
                  meta: {
                    hide: true,
                    label: t('shared.behaviorExperiments.title'),
                  },
                },
                {
                  name: 'portalTests',
                  list: '/tools/portalTests',
                  meta: { hide: true, label: t('assessments.hub.tab') },
                },
                {
                  name: 'supportingCycles',
                  create: '/tools/supportingCycles/create',
                  list: '/tools/supportingCycles',
                  meta: {
                    hide: true,
                    label: t('pages.supportingCycle.supportingCyclesShort'),
                  },
                },
                {
                  name: 'exposures',
                  create: '/tools/exposures/create',
                  list: '/tools/exposures',
                  edit: '/tools/exposures/edit',
                  meta: {
                    hide: true,
                    label: t('shared.exposure.titlePlural'),
                  },
                },
                {
                  name: 'practice',
                  meta: {
                    label: t('practice.practice'),
                    icon: <ApartmentOutlined />,
                  },
                },
                {
                  parentName: 'practice',
                  name: 'practiceUsers',
                  list: 'practice/practiceUsers',
                },
                {
                  parentName: 'practice',
                  name: 'practiceInfo',
                  list: 'practice/practiceinfo/edit',
                },
                { parentName: 'practice', name: 'locations' },
                // E57 · Settings — My profile, My tools, Subscription (administrator), Invites
                {
                  name: 'settings',
                  meta: {
                    label: t('portalMenu.settings'),
                    icon: <SettingOutlined />,
                  },
                },
                {
                  parentName: 'settings',
                  name: 'profile',
                  list: 'profile/edit',
                  meta: { label: t('portalMenu.myProfile') },
                },
                {
                  parentName: 'settings',
                  name: 'myTools',
                  list: '/settings/my-tools',
                  meta: {
                    label: t('myTools.menu'),
                    icon: <AppstoreOutlined />,
                  },
                },
                {
                  parentName: 'settings',
                  name: 'portalSubscriptions',
                  list: 'portalSubscriptions',
                  meta: {
                    label: t('portalSubscriptions.title'),
                  },
                },
                {
                  parentName: 'settings',
                  name: 'invites',
                  create: '/invites/create',
                  list: '/invites',
                },
              ]}
              i18nProvider={i18nProvider}
              options={{
                disableTelemetry: true,
              }}
            >
              <LocaleRefetch language={i18n.languages[0]} />
              <Routes>
                <Route path="/join/:token" element={<InviteJoin />} />
                <Route path="/join/users/:token" element={<InviteJoin />} />

                <Route
                  element={
                    <Authenticated key="logged-out" fallback={<Outlet />}>
                      <NavigateToResource resource="clients" />
                    </Authenticated>
                  }
                >
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />

                  <Route
                    path="/reset-password"
                    element={<PasswordResetPage />}
                  />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/deep/auth" element={<VerifyEmail />} />
                  <Route path="*" element={<ErrorComponent />} />
                </Route>
                <Route
                  element={
                    <Authenticated
                      key="dashboard"
                      fallback={<Navigate to="/login" />}
                    >
                      <Outlet />
                    </Authenticated>
                  }
                >
                  {/* E33 · the backoffice — the staff console: its own shell, outside the therapist
                      layout and never in the therapist menu; a non-member sees "page not found" */}
                  <Route path="/backoffice" element={<BackofficeLayout />}>
                    <Route index element={<Navigate to="members" replace />} />
                    <Route path="members" element={<BackofficeMembers />} />
                    <Route path="erasure" element={<BackofficeErase />} />
                    <Route
                      path="erasure/log"
                      element={<BackofficeErasureLog />}
                    />
                    {/* E34 block 2a — the CopingCard worksheet library (PublishWorksheetTemplate) */}
                    <Route path="worksheets" element={<BackofficeWorksheets />} />
                    <Route
                      path="worksheets/new"
                      element={<BackofficeWorksheetPublish />}
                    />
                    <Route
                      path="worksheets/:templateId/edit"
                      element={<BackofficeWorksheetPublish />}
                    />
                    {/* E37 — the diary card template catalogue (PublishDiaryCardTemplate) */}
                    <Route
                      path="diary-card-templates"
                      element={<BackofficeDiaryCardTemplates />}
                    />
                    {/* E35 — the instrument definitions (PublishInstrument) */}
                    <Route path="instruments" element={<BackofficeInstruments />} />
                    {/* E41 — the behavioral-activation activity library (PublishActivityLibrary) */}
                    <Route path="activity-library" element={<BackofficeActivityLibrary />} />
                    {/* E36 — the values card-sort decks (PublishValuesDeck) */}
                    <Route path="values-decks" element={<BackofficeValuesDecks />} />
                    {/* E34 block 5a — access rules, the course review, the practices' course capabilities */}
                    <Route path="courses" element={<BackofficeCourses />} />
                    <Route path="courses/review" element={<BackofficeCourseReview />} />
                    <Route path="practices" element={<BackofficePractices />} />
                    <Route
                      path="practices/:practiceKey"
                      element={<BackofficePracticeCapabilities />}
                    />
                  </Route>
                  <Route
                    element={
                      <CcThemedLayoutV2>
                        <Outlet />
                      </CcThemedLayoutV2>
                    }
                  >
                    <Route path="/initial-setup" element={<InitialSetup />} />
                    <Route path="/clients" element={<Outlet />}>
                      <Route index element={<ClientList />} />
                      <Route
                        path="unassigned"
                        element={<UnassignedClientList />}
                      />
                      <Route path="show/:id" element={<ClientShow />} />
                    </Route>
                    <Route path="/sessions" element={<Outlet />}>
                      <Route index element={<SessionsList />} />
                      <Route path="create" element={<SessionsCreate />} />
                    </Route>
                    {/* E57 (Q-pia-3 A) — the ten Tools lists are Caseload › All records: each old list address opens it
                        with its tool ticked; the create (and show / edit) addresses still open the tool's own pages */}
                    <Route path="/tools" element={<Outlet />}>
                      <Route path="conceptualizations" element={<Outlet />}>
                        <Route index element={<LegacyToolListRedirect resource="conceptualizations" />} />
                        <Route
                          path="create"
                          element={<CreateConceptualization />}
                        />
                      </Route>

                      <Route path="portalCards" element={<Outlet />}>
                        <Route index element={<LegacyToolListRedirect resource="portalCards" />} />
                        <Route path="create" element={<CreateCopingCard />} />
                      </Route>

                      <Route path="abcModels" element={<Outlet />}>
                        <Route index element={<LegacyToolListRedirect resource="abcModels" />} />
                        <Route path="create" element={<AbcModelCreate />} />
                      </Route>

                      <Route path="portalThoughts" element={<Outlet />}>
                        <Route index element={<LegacyToolListRedirect resource="portalThoughts" />} />
                        <Route path="create" element={<CreateThought />} />
                      </Route>

                      <Route path="worryTrees" element={<Outlet />}>
                        <Route index element={<LegacyToolListRedirect resource="worryTrees" />} />
                        <Route path="create" element={<WorryTreeCreate />} />
                      </Route>

                      <Route path="downwardArrows" element={<Outlet />}>
                        <Route index element={<LegacyToolListRedirect resource="downwardArrows" />} />
                        <Route
                          path="create"
                          element={<DownwardArrowCreate />}
                        />
                      </Route>

                      <Route path="behaviorExperiments" element={<Outlet />}>
                        <Route index element={<LegacyToolListRedirect resource="behaviorExperiments" />} />
                        <Route
                          path="create"
                          element={<BehaviorExperimentCreate />}
                        />
                        <Route
                          path="show"
                          element={<BehaviorExperimentView />}
                        />
                      </Route>

                      <Route path="portalTests" element={<Outlet />}>
                        <Route index element={<LegacyToolListRedirect resource="portalTests" />} />
                      </Route>

                      <Route path="supportingCycles" element={<Outlet />}>
                        <Route index element={<LegacyToolListRedirect resource="supportingCycles" />} />
                        <Route
                          path="create"
                          element={<SupportingCyclesCreate />}
                        />
                      </Route>

                      <Route path="exposures" element={<Outlet />}>
                        <Route index element={<LegacyToolListRedirect resource="exposures" />} />
                        <Route path="create" element={<ExposureCreate />} />
                        <Route
                          path="edit"
                          element={<EditExposureSituation />}
                        />
                      </Route>
                    </Route>

                    {/* E57 · Caseload — Overview and All records; the Homework tab is the E34 board at /homework */}
                    <Route path="/caseload" element={<Outlet />}>
                      <Route index element={<CaseloadOverviewPage />} />
                      <Route path="records" element={<AllRecordsPage />} />
                    </Route>
                    {/* E57 · Settings › My tools */}
                    <Route path="/settings" element={<Outlet />}>
                      <Route index element={<Navigate to="/settings/my-tools" replace />} />
                      <Route path="my-tools" element={<MyToolsPage />} />
                    </Route>

                    <Route path="/practice" element={<Outlet />}>
                      <Route path="practiceUsers" element={<UserList />} />
                      <Route
                        path="practiceinfo/edit"
                        element={<PracticeEdit />}
                      />
                      {/* the board moved to the top-level /homework (Q-b1b-3 A); the old address still lands on it */}
                      <Route
                        path="homework"
                        element={<Navigate to="/homework" replace />}
                      />
                    </Route>
                    {/* E34 · the Homework board — Caseload's Homework tab (E57), the same address, the board unchanged */}
                    <Route
                      path="/homework"
                      element={
                        <>
                          <CaseloadTabs active="homework" />
                          <PracticeHomeworkBoardPage />
                        </>
                      }
                    />
                    {/* E34 block 2a — the worksheet library and its Builder */}
                    <Route path="/worksheets" element={<Outlet />}>
                      <Route index element={<WorksheetLibraryPage />} />
                      <Route path="new" element={<WorksheetBuilderPage />} />
                      {/* block 2b — one response, opened from the client hub's Worksheets & documents tab */}
                      <Route
                        path="response/:submissionId"
                        element={<WorksheetResponsePage />}
                      />
                      {/* D-entries-over-time-2026-10-07 — a repeated worksheet's numbers over time, from the client hub */}
                      <Route
                        path="over-time/:subject/:templateId"
                        element={<WorksheetOverTimePage />}
                      />
                      <Route
                        path=":templateId/edit"
                        element={<WorksheetBuilderPage />}
                      />
                      {/* Q-paper-2 A — the printable rendering of a template version (?version=) */}
                      <Route
                        path=":templateId/print"
                        element={<WorksheetPrintView />}
                      />
                    </Route>
                    {/* E34 block 3a — the pathway library, its Builder, Outcomes, and one enrolment's progress */}
                    <Route path="/pathways" element={<Outlet />}>
                      <Route index element={<PathwayLibraryPage />} />
                      <Route path="new" element={<PathwayBuilderPage />} />
                      <Route path="outcomes" element={<PathwayOutcomesPage />} />
                      <Route
                        path="progress/:enrollmentId"
                        element={<PathwayProgressPage />}
                      />
                      <Route
                        path=":pathwayId/edit"
                        element={<PathwayBuilderPage />}
                      />
                    </Route>
                    <Route path="/profile" element={<Outlet />}>
                      <Route path="edit" element={<ProfileEdit />} />
                    </Route>
                    <Route path="/messages" element={<MessageList />} />
                    <Route path="/invites" element={<Outlet />}>
                      <Route index element={<InviteList />} />
                      <Route path="create" element={<CreateInvite />} />
                    </Route>
                    <Route
                      path="/portalSubscriptions"
                      element={<PortalSubscriptionList />}
                    />

                    <Route path="/" element={<ClientList />} />
                    <Route path="*" element={<ErrorComponent />} />
                  </Route>
                </Route>
              </Routes>
              {/*
                Warns before navigating away from a form with unsaved changes, using the existing
                `warnWhenUnsavedChanges` copy (founder decision, 2026-09-01). Until now `warnWhen`
                was set by several forms but only the sider's LOGOUT honoured it — ordinary
                navigation discarded the work silently. Required by the move from autosave to an
                explicit Save: taking autosave away without this would lose therapists' typing.
              */}
              <UnsavedChangesNotifier translationKey="warnWhenUnsavedChanges" />
            </Refine>
          </AntdApp>
        </ConfigProvider>
      </TherapistActivityProvider>
    </BrowserRouter>
  ) : null;
}

export default App;
